import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Paper,
  Alert,
  Divider,
  Badge,
} from '@mui/material';
import {
  Close,
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
  FilterList,
  Search,
  AccountTree,
  Hub,
  CheckCircle,
  Fullscreen,
  Translate,
  NavigateBefore,
  NavigateNext,
} from '@mui/icons-material';
import ForceGraph2D from 'react-force-graph-2d';
import { graphApi } from '../../api/graphApi';
import { analysisApi } from '../../api/analysisApi';

// Edge color map by relationship type
const EDGE_COLORS = {
  is_derived_from: '#2196F3',
  is_verification_of: '#4CAF50',
  has_source: '#FF9800',
  refers_to: '#9E9E9E',
  related_to: '#9C27B0',
};

const EDGE_LABELS = {
  is_derived_from: 'Derived From',
  is_verification_of: 'Verifies',
  has_source: 'Has Source',
  refers_to: 'Refers To',
  related_to: 'Related To',
};

const EDGE_DESCRIPTIONS = {
  is_derived_from: 'Derived from a higher-level requirement. Downward traceability.',
  is_verification_of: 'Verifies compliance with another requirement.',
  has_source: 'Based on this source standard or document.',
  refers_to: 'Informational reference, not hierarchical.',
  related_to: 'Generic relationship between related requirements.',
};

const normalizeRelationshipType = (type) => {
  if (!type) return 'related_to';
  if (EDGE_LABELS[type]) return type;

  const normalized = String(type).toLowerCase();

  if (normalized.includes('isverificationof')) return 'is_verification_of';
  if (normalized.includes('hassource')) return 'has_source';
  if (normalized.includes('refersto')) return 'refers_to';
  if (normalized.includes('isderivedfrom') || normalized.includes('hasrequirement')) {
    return 'is_derived_from';
  }
  if (normalized.includes('related')) return 'related_to';

  return 'related_to';
};

const COMPACT_BLUE_COLOR = '#2196f3';
const DOUBLE_CLICK_MS = 800;
const HIERARCHICAL_LEVEL_GAP = 190;
const HIERARCHICAL_NODE_GAP = 170;
const HIERARCHICAL_COMPONENT_GAP = 260;

const getEndpointId = (endpoint) => (typeof endpoint === 'object' ? endpoint?.id : endpoint);
const buildEdgeKey = (sourceId, targetId, type) => `${sourceId}::${targetId}::${type || ''}`;
const edgeKeyFromLink = (link) => buildEdgeKey(getEndpointId(link.source), getEndpointId(link.target), link.type);
const getNodeDbId = (node) => node?.dbId ?? node?.db_id ?? node?.requirement_db_id ?? null;
const average = (values) => {
  if (!values.length) return null;
  return values.reduce((acc, value) => acc + value, 0) / values.length;
};

const computeHierarchicalPositions = (nodes, links) => {
  if (!nodes?.length) return new Map();

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const nodeIds = Array.from(nodeById.keys());
  const nodeIdSet = new Set(nodeIds);

  const incoming = new Map();
  const outgoing = new Map();
  const undirected = new Map();

  nodeIds.forEach((id) => {
    incoming.set(id, new Set());
    outgoing.set(id, new Set());
    undirected.set(id, new Set());
  });

  links.forEach((link) => {
    const src = getEndpointId(link.source);
    const tgt = getEndpointId(link.target);
    if (!src || !tgt || src === tgt) return;
    if (!nodeIdSet.has(src) || !nodeIdSet.has(tgt)) return;

    outgoing.get(src).add(tgt);
    incoming.get(tgt).add(src);
    undirected.get(src).add(tgt);
    undirected.get(tgt).add(src);
  });

  const components = [];
  const seen = new Set();
  nodeIds.forEach((startId) => {
    if (seen.has(startId)) return;
    const stack = [startId];
    const component = [];
    seen.add(startId);

    while (stack.length) {
      const id = stack.pop();
      component.push(id);
      undirected.get(id)?.forEach((neighborId) => {
        if (seen.has(neighborId)) return;
        seen.add(neighborId);
        stack.push(neighborId);
      });
    }

    components.push(component);
  });

  const componentLayouts = components.map((componentIds) => {
    const componentSet = new Set(componentIds);
    const inDegreeInComponent = (id) =>
      [...(incoming.get(id) || [])].filter((parentId) => componentSet.has(parentId)).length;

    const roots = componentIds.filter((id) => inDegreeInComponent(id) === 0);
    if (!roots.length) {
      const fallbackRoot = [...componentIds].sort((a, b) => {
        const aScore = (outgoing.get(a)?.size || 0) - (incoming.get(a)?.size || 0);
        const bScore = (outgoing.get(b)?.size || 0) - (incoming.get(b)?.size || 0);
        if (aScore !== bScore) return bScore - aScore;
        return String(a).localeCompare(String(b));
      })[0];
      roots.push(fallbackRoot);
    }

    const depthById = new Map();
    const queue = [];
    roots
      .slice()
      .sort((a, b) => String(a).localeCompare(String(b)))
      .forEach((rootId) => {
        depthById.set(rootId, 0);
        queue.push(rootId);
      });

    while (queue.length) {
      const id = queue.shift();
      const currentDepth = depthById.get(id) || 0;
      (outgoing.get(id) || new Set()).forEach((targetId) => {
        if (!componentSet.has(targetId)) return;
        const candidateDepth = currentDepth + 1;
        const previousDepth = depthById.get(targetId);
        if (previousDepth === undefined || candidateDepth < previousDepth) {
          depthById.set(targetId, candidateDepth);
          queue.push(targetId);
        }
      });
    }

    let unresolved = componentIds.filter((id) => !depthById.has(id));
    while (unresolved.length) {
      let progressed = false;

      unresolved.forEach((id) => {
        const resolvedNeighbors = [...(undirected.get(id) || [])].filter(
          (neighborId) => componentSet.has(neighborId) && depthById.has(neighborId)
        );
        if (!resolvedNeighbors.length) return;

        const neighborDepths = resolvedNeighbors.map((neighborId) => depthById.get(neighborId));
        const neighborAvgDepth = Math.round(average(neighborDepths) || 0);
        depthById.set(id, Math.max(0, neighborAvgDepth));
        progressed = true;
      });

      if (!progressed) {
        unresolved.forEach((id) => depthById.set(id, 0));
        break;
      }

      unresolved = componentIds.filter((id) => !depthById.has(id));
    }

    const minDepth = Math.min(...Array.from(depthById.values()));
    if (minDepth !== 0) {
      depthById.forEach((depth, id) => {
        depthById.set(id, depth - minDepth);
      });
    }

    const maxDepth = Math.max(...Array.from(depthById.values()));
    const layers = new Map();
    for (let depth = 0; depth <= maxDepth; depth += 1) {
      layers.set(depth, []);
    }

    componentIds.forEach((id) => {
      const depth = depthById.get(id) || 0;
      if (!layers.has(depth)) layers.set(depth, []);
      layers.get(depth).push(id);
    });

    const sortKey = (id) => {
      const node = nodeById.get(id);
      return String(node?.notation || node?.label_english || node?.label_original || node?.label || id);
    };

    const layerOrder = new Map();
    layers.forEach((ids, depth) => {
      layerOrder.set(depth, [...ids].sort((a, b) => sortKey(a).localeCompare(sortKey(b))));
    });

    const sortByBarycenter = (depth, direction) => {
      const ids = [...(layerOrder.get(depth) || [])];
      if (ids.length <= 1) return;

      const neighborDepth = direction === 'up' ? depth - 1 : depth + 1;
      const neighborLayer = layerOrder.get(neighborDepth) || [];
      if (!neighborLayer.length) return;

      const neighborIndex = new Map(neighborLayer.map((id, index) => [id, index]));
      const currentIndex = new Map(ids.map((id, index) => [id, index]));

      ids.sort((a, b) => {
        const aNeighbors = direction === 'up' ? incoming.get(a) : outgoing.get(a);
        const bNeighbors = direction === 'up' ? incoming.get(b) : outgoing.get(b);

        const aScores = [...(aNeighbors || [])]
          .filter((id) => depthById.get(id) === neighborDepth && neighborIndex.has(id))
          .map((id) => neighborIndex.get(id));
        const bScores = [...(bNeighbors || [])]
          .filter((id) => depthById.get(id) === neighborDepth && neighborIndex.has(id))
          .map((id) => neighborIndex.get(id));

        const aBarycenter = average(aScores);
        const bBarycenter = average(bScores);

        if (aBarycenter !== null && bBarycenter !== null && aBarycenter !== bBarycenter) {
          return aBarycenter - bBarycenter;
        }
        if (aBarycenter !== null && bBarycenter === null) return -1;
        if (aBarycenter === null && bBarycenter !== null) return 1;

        return (currentIndex.get(a) || 0) - (currentIndex.get(b) || 0);
      });

      layerOrder.set(depth, ids);
    };

    for (let iteration = 0; iteration < 4; iteration += 1) {
      for (let depth = 1; depth <= maxDepth; depth += 1) {
        sortByBarycenter(depth, 'up');
      }
      for (let depth = maxDepth - 1; depth >= 0; depth -= 1) {
        sortByBarycenter(depth, 'down');
      }
    }

    const maxLayerWidth = Math.max(
      1,
      ...Array.from(layerOrder.values()).map((ids) => ids.length)
    );

    return {
      anchorId: roots[0],
      nodeCount: componentIds.length,
      maxLayerWidth,
      layerOrder,
    };
  });

  componentLayouts.sort((a, b) => {
    if (a.maxLayerWidth !== b.maxLayerWidth) return b.maxLayerWidth - a.maxLayerWidth;
    if (a.nodeCount !== b.nodeCount) return b.nodeCount - a.nodeCount;
    return String(a.anchorId).localeCompare(String(b.anchorId));
  });

  let previousWidth = 0;
  let centerX = 0;
  const placedComponents = componentLayouts.map((layout, index) => {
    const width = Math.max(1, layout.maxLayerWidth - 1) * HIERARCHICAL_NODE_GAP + HIERARCHICAL_NODE_GAP;
    if (index === 0) {
      centerX = 0;
    } else {
      centerX += previousWidth / 2 + HIERARCHICAL_COMPONENT_GAP + width / 2;
    }
    previousWidth = width;
    return { ...layout, width, centerX };
  });

  const minLeft = Math.min(...placedComponents.map((c) => c.centerX - c.width / 2));
  const maxRight = Math.max(...placedComponents.map((c) => c.centerX + c.width / 2));
  const horizontalShift = -((minLeft + maxRight) / 2);

  const positions = new Map();
  placedComponents.forEach((component) => {
    component.layerOrder.forEach((ids, depth) => {
      const levelOffset = (ids.length - 1) / 2;
      ids.forEach((id, index) => {
        const x = component.centerX + horizontalShift + (index - levelOffset) * HIERARCHICAL_NODE_GAP;
        const y = depth * HIERARCHICAL_LEVEL_GAP;
        positions.set(id, { x, y });
      });
    });
  });

  return positions;
};

const computeClusterPositions = (nodes, links) => {
  if (!nodes?.length) return new Map();

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const nodeIds = Array.from(nodeById.keys());
  const nodeIdSet = new Set(nodeIds);
  const adjacency = new Map(nodeIds.map((id) => [id, new Set()]));

  links.forEach((link) => {
    const src = getEndpointId(link.source);
    const tgt = getEndpointId(link.target);
    if (!src || !tgt || src === tgt) return;
    if (!nodeIdSet.has(src) || !nodeIdSet.has(tgt)) return;

    adjacency.get(src).add(tgt);
    adjacency.get(tgt).add(src);
  });

  const components = [];
  const visited = new Set();
  nodeIds.forEach((startId) => {
    if (visited.has(startId)) return;

    const stack = [startId];
    const component = [];
    visited.add(startId);

    while (stack.length) {
      const id = stack.pop();
      component.push(id);
      (adjacency.get(id) || new Set()).forEach((neighborId) => {
        if (visited.has(neighborId)) return;
        visited.add(neighborId);
        stack.push(neighborId);
      });
    }

    components.push(component);
  });

  const sortKey = (id) => {
    const node = nodeById.get(id);
    return String(node?.notation || node?.label_english || node?.label_original || node?.label || id);
  };

  components.sort((a, b) => {
    if (a.length !== b.length) return b.length - a.length;
    return sortKey(a[0]).localeCompare(sortKey(b[0]));
  });

  const positions = new Map();
  const totalComponents = components.length;
  const cols = Math.max(1, Math.ceil(Math.sqrt(totalComponents)));
  const rows = Math.max(1, Math.ceil(totalComponents / cols));
  const spacingX = 920;
  const spacingY = 760;

  components.forEach((component, index) => {
    const row = Math.floor(index / cols);
    const col = index % cols;

    const centerX = (col - (cols - 1) / 2) * spacingX;
    const centerY = (row - (rows - 1) / 2) * spacingY;

    const orderedIds = [...component].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    const componentRadius = orderedIds.length <= 1 ? 0 : Math.max(150, Math.sqrt(orderedIds.length) * 95);

    orderedIds.forEach((id, nodeIndex) => {
      if (orderedIds.length === 1) {
        positions.set(id, { x: centerX, y: centerY });
        return;
      }

      const angle = (nodeIndex / orderedIds.length) * Math.PI * 2;
      positions.set(id, {
        x: centerX + Math.cos(angle) * componentRadius,
        y: centerY + Math.sin(angle) * componentRadius,
      });
    });
  });

  return positions;
};

const GraphViewDialog = ({ open, onClose, fileId, focusRequirementId, fileName }) => {
  const graphContainerRef = useRef(null);
  const [graphDimensions, setGraphDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = graphContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setGraphDimensions({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const graphRef = useRef(null);
  const lastNodeClickRef = useRef({ id: null, ts: 0 });
  const baseGraphRef = useRef({ nodeIds: new Set(), edgeKeys: new Set() });
  const expansionCoverageRef = useRef(new Map());
  const collapseCoverageRef = useRef(new Map());
  const expandedNodesRef = useRef(new Set());
  const collapsedNodesRef = useRef(new Set());
  const hasInitialFocusCenteredRef = useRef(false);
  const activeSubgraphNodeIdsRef = useRef(new Set());
  const originalSubgraphNodeIdsRef = useRef(new Set());
  const originalFocusNodeIdRef = useRef(null);
  const previousLayoutRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [graphView, setGraphView] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandingNode, setExpandingNode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSelection, setSearchSelection] = useState(null);
  const [pinnedSearchNodeId, setPinnedSearchNodeId] = useState(null);
  const [activeLayout, setActiveLayout] = useState('hierarchical');
  const [activeFilters, setActiveFilters] = useState({
    relationship_type: [],
    smart_compliant: null,
    translation_status: null,
    validation_status: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [highlightNodes, setHighlightNodes] = useState(new Set());
  const [highlightLinks, setHighlightLinks] = useState(new Set());
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  // 'notation' | 'original' | 'english'
  const [labelMode, setLabelMode] = useState('english');
  const [nodeDetail, setNodeDetail] = useState(null);
  const [nodeDetailLoading, setNodeDetailLoading] = useState(false);
  const [graphDepth, setGraphDepth] = useState(3);
  const [compactMode, setCompactMode] = useState(false);
  const [currentSubgraphIndex, setCurrentSubgraphIndex] = useState(0);
  const forceClusterPositions = useMemo(
    () => computeClusterPositions(graphData.nodes, graphData.links),
    [graphData.nodes, graphData.links]
  );

  useEffect(() => {
    expandedNodesRef.current = expandedNodes;
  }, [expandedNodes]);

  useEffect(() => {
    collapsedNodesRef.current = collapsedNodes;
  }, [collapsedNodes]);

  // Load graph data when dialog opens or depth changes
  useEffect(() => {
    if (open && fileId) {
      loadGraphData();
    }
    return () => {
      setGraphData({ nodes: [], links: [] });
      setGraphView(null);
      setSelectedNode(null);
      setExpandedNodes(new Set());
      setCollapsedNodes(new Set());
      setSearchTerm('');
      setSearchSelection(null);
      setPinnedSearchNodeId(null);
      setError('');
      setHighlightNodes(new Set());
      setHighlightLinks(new Set());
      setNodeDetail(null);
      setCurrentSubgraphIndex(0);
      baseGraphRef.current = { nodeIds: new Set(), edgeKeys: new Set() };
      expansionCoverageRef.current = new Map();
      collapseCoverageRef.current = new Map();
      expandedNodesRef.current = new Set();
      collapsedNodesRef.current = new Set();
      hasInitialFocusCenteredRef.current = false;
      activeSubgraphNodeIdsRef.current = new Set();
      originalSubgraphNodeIdsRef.current = new Set();
      originalFocusNodeIdRef.current = null;
      previousLayoutRef.current = null;
    };
  }, [open, fileId, graphDepth]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGraphData = async () => {
    hasInitialFocusCenteredRef.current = false;
    activeSubgraphNodeIdsRef.current = new Set();
    originalSubgraphNodeIdsRef.current = new Set();
    originalFocusNodeIdRef.current = null;
    previousLayoutRef.current = null;
    setPinnedSearchNodeId(null);
    setLoading(true);
    setError('');
    try {
      const params = { depth: graphDepth };
      if (Number(graphDepth) === 3) {
        params.no_limit = true;
      }
      if (focusRequirementId) {
        params.focus_requirement_id = focusRequirementId;
      }
      const response = await graphApi.getGraphData(fileId, params);
      if (response.status === 'success') {
        const view = response.data.graphView;
        setGraphView(view);
        if (view?.focusNode) {
          activeSubgraphNodeIdsRef.current = new Set([view.focusNode]);
          originalSubgraphNodeIdsRef.current = new Set([view.focusNode]);
          originalFocusNodeIdRef.current = view.focusNode;
        }
        setActiveLayout('hierarchical');

        // Transform to react-force-graph format — clear positions so DAG layout starts fresh
        const nodes = view.nodes.map((n) => ({
          ...n,
          val: n.visual.importance || 1,
          x: undefined,
          y: undefined,
          fx: undefined,
          fy: undefined,
        }));
        const links = view.edges.map((e) => ({
          ...e,
          source: e.source,
          target: e.target,
        }));

        baseGraphRef.current = {
          nodeIds: new Set(nodes.map((n) => n.id)),
          edgeKeys: new Set(links.map((l) => buildEdgeKey(l.source, l.target, l.type))),
        };
        expansionCoverageRef.current = new Map();
        collapseCoverageRef.current = new Map();
        setExpandedNodes(new Set());
        setCollapsedNodes(new Set());
        setCurrentSubgraphIndex(0);
        expandedNodesRef.current = new Set();
        collapsedNodesRef.current = new Set();

        setGraphData({ nodes, links });
      } else {
        setError(response.error || 'Failed to load graph data');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error loading graph');
    } finally {
      setLoading(false);
    }
  };

  // Configure d3 forces after graph mounts — optimised for readability
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0) {
      const fg = graphRef.current;
      if (activeLayout === 'hierarchical') {
        // In hierarchical mode positions are precomputed; keep physics soft.
        fg.d3Force('link')?.distance(150).strength(0.04);
        fg.d3Force('charge')?.strength(-80).distanceMax(260);
        fg.d3Force('center')?.strength(0.01);
        fg.d3Force('collision', null);
      } else {
        fg.d3Force('collision', null);
        fg.d3Force('link')?.distance(180).strength(0.35);
        fg.d3Force('charge')?.strength(-500).distanceMax(550);
        fg.d3Force('center')?.strength(0.04);
      }
    }
  }, [graphData, activeLayout]);

  // When layout changes, clear node positions so the new layout starts fresh
  useEffect(() => {
    const preserveSelectedSubgraphCenter =
      activeSubgraphNodeIdsRef.current.size > 0 ||
      originalSubgraphNodeIdsRef.current.size > 0;

    if (graphData.nodes.length > 0) {
      if (activeLayout === 'force-directed') {
        graphData.nodes.forEach((n) => {
          const seededPos = forceClusterPositions.get(n.id);
          if (seededPos) {
            n.x = seededPos.x;
            n.y = seededPos.y;
          }
          delete n.fx;
          delete n.fy;
          n.vx = 0;
          n.vy = 0;
        });
      } else {
        graphData.nodes.forEach((n) => {
          delete n.x; delete n.y; delete n.fx; delete n.fy;
          delete n.vx; delete n.vy;
        });
      }
    }

    const reheatTimer = setTimeout(() => {
      graphRef.current?.d3ReheatSimulation();
    }, 50);

    // Ensure nodes are brought back into view after changing layout (especially force-directed).
    const fitTimer = setTimeout(() => {
      // When switching layouts from a selected subgraph, avoid global fit because
      // it overrides the targeted centering logic and recenters the whole graph.
      if (preserveSelectedSubgraphCenter) return;

      if (!graphRef.current || filteredGraphData.nodes.length === 0) return;
      const hasPositionedNodes = filteredGraphData.nodes.some(
        (node) => Number.isFinite(node.x) && Number.isFinite(node.y)
      );
      if (!hasPositionedNodes) return;

      try {
        graphRef.current.zoomToFit(activeLayout === 'hierarchical' ? 420 : 620, 80);
      } catch (err) {
        console.warn('zoomToFit failed after layout switch:', err);
      }
    }, activeLayout === 'hierarchical' ? 120 : 320);

    return () => {
      clearTimeout(reheatTimer);
      clearTimeout(fitTimer);
    };
  }, [activeLayout, forceClusterPositions]); // eslint-disable-line react-hooks/exhaustive-deps

  const getNeighborhoodCoverage = useCallback(
    (rootNodeId, depth = 3) => {
      const visited = new Set();
      const edgeKeys = new Set();

      if (!rootNodeId) {
        return { nodeIds: visited, edgeKeys };
      }

      visited.add(rootNodeId);
      let frontier = new Set([rootNodeId]);

      for (let level = 0; level < depth; level += 1) {
        const nextFrontier = new Set();

        for (const link of graphData.links) {
          const src = getEndpointId(link.source);
          const tgt = getEndpointId(link.target);
          if (!src || !tgt) continue;

          const touchesFrontier = frontier.has(src) || frontier.has(tgt);
          if (!touchesFrontier) continue;

          if (!visited.has(src)) nextFrontier.add(src);
          if (!visited.has(tgt)) nextFrontier.add(tgt);
          edgeKeys.add(buildEdgeKey(src, tgt, link.type));
        }

        if (nextFrontier.size === 0) break;
        for (const id of nextFrontier) {
          visited.add(id);
        }
        frontier = nextFrontier;
      }

      // Ensure all internal family edges are included.
      graphData.links.forEach((link) => {
        const src = getEndpointId(link.source);
        const tgt = getEndpointId(link.target);
        if (!src || !tgt) return;
        if (visited.has(src) && visited.has(tgt)) {
          edgeKeys.add(buildEdgeKey(src, tgt, link.type));
        }
      });

      return { nodeIds: visited, edgeKeys };
    },
    [graphData.links]
  );

  const handleNodeExpand = useCallback(
    async (node) => {
      const nodeId = node?.id;
      if (!nodeId) return;

      const nodeFromState = graphData.nodes.find((n) => n.id === nodeId);
      const nodeDbId = getNodeDbId(node) ?? getNodeDbId(nodeFromState);
      setError('');

      if (!compactMode) {
        const isCollapsed = collapsedNodesRef.current.has(nodeId);

        if (isCollapsed) {
          collapseCoverageRef.current.delete(nodeId);
          setCollapsedNodes((prev) => {
            const next = new Set(prev);
            next.delete(nodeId);
            collapsedNodesRef.current = next;
            return next;
          });
          return;
        }

        let coverage = getNeighborhoodCoverage(nodeId, 3);

        if (nodeDbId) {
          setExpandingNode(nodeId);
          try {
            const response = await graphApi.expandNode(nodeDbId, { depth: 3 });
            if (response.status === 'success') {
              const { nodes: familyNodes, edges: familyEdges } = response.data;

              coverage = {
                nodeIds: new Set([...coverage.nodeIds, ...familyNodes.map((n) => n.id)]),
                edgeKeys: new Set([
                  ...coverage.edgeKeys,
                  ...familyEdges.map((e) => buildEdgeKey(e.source, e.target, e.type)),
                ]),
              };

              // Keep the full graph enriched so toggling back to expanded is immediate.
              setGraphData((prev) => {
                const existingIds = new Set(prev.nodes.map((n) => n.id));
                const nodesToAdd = familyNodes
                  .filter((n) => !existingIds.has(n.id))
                  .map((n) => ({ ...n, val: n.visual.importance || 1 }));

                const existingEdgeKeys = new Set(prev.links.map((l) => edgeKeyFromLink(l)));
                const linksToAdd = familyEdges
                  .filter((e) => !existingEdgeKeys.has(buildEdgeKey(e.source, e.target, e.type)))
                  .map((e) => ({ ...e, source: e.source, target: e.target }));

                return {
                  nodes: [...prev.nodes, ...nodesToAdd],
                  links: [...prev.links, ...linksToAdd],
                };
              });
            } else {
              setError(response.error || 'Error toggling node state');
            }
          } catch (err) {
            console.error('Error toggling node in full view:', err);
            setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Error toggling node state');
          } finally {
            setExpandingNode(null);
          }
        }

        collapseCoverageRef.current.set(nodeId, {
          nodeIds: new Set([...coverage.nodeIds].filter((id) => id !== nodeId)),
          edgeKeys: coverage.edgeKeys,
        });

        setCollapsedNodes((prev) => {
          const next = new Set(prev);
          next.add(nodeId);
          collapsedNodesRef.current = next;
          return next;
        });

        return;
      }

      const isExpanded = expandedNodesRef.current.has(nodeId);

      if (isExpanded) {
        expansionCoverageRef.current.delete(nodeId);

        const keepNodeIds = new Set(baseGraphRef.current.nodeIds);
        const keepEdgeKeys = new Set(baseGraphRef.current.edgeKeys);

        expansionCoverageRef.current.forEach((coverage) => {
          coverage.nodeIds.forEach((id) => keepNodeIds.add(id));
          coverage.edgeKeys.forEach((key) => keepEdgeKeys.add(key));
        });

        setGraphData((prev) => ({
          nodes: prev.nodes.filter((n) => keepNodeIds.has(n.id)),
          links: prev.links.filter((l) => keepEdgeKeys.has(edgeKeyFromLink(l))),
        }));

        setTimeout(() => {
          if (graphRef.current) {
            graphRef.current.d3ReheatSimulation();
            graphRef.current.zoomToFit(350, 80);
          }
        }, 60);

        setExpandedNodes((prev) => {
          const next = new Set(prev);
          next.delete(nodeId);
          expandedNodesRef.current = next;
          return next;
        });

        // Reset highlights to avoid references to removed links/nodes.
        setHighlightNodes(
          pinnedSearchNodeId ? new Set([pinnedSearchNodeId]) : new Set()
        );
        setHighlightLinks(new Set());

        if (selectedNode && !keepNodeIds.has(selectedNode.id)) {
          setSelectedNode(null);
          setNodeDetail(null);
        }

        return;
      }

      let coverage = getNeighborhoodCoverage(nodeId, 3);

      if (nodeDbId) {
        setExpandingNode(nodeId);
        try {
          const response = await graphApi.expandNode(nodeDbId, { depth: 3 });
          if (response.status === 'success') {
            const { nodes: newNodes, edges: newEdges } = response.data;

            coverage = {
              nodeIds: new Set([...coverage.nodeIds, ...newNodes.map((n) => n.id)]),
              edgeKeys: new Set([
                ...coverage.edgeKeys,
                ...newEdges.map((e) => buildEdgeKey(e.source, e.target, e.type)),
              ]),
            };

            setGraphData((prev) => {
              const existingIds = new Set(prev.nodes.map((n) => n.id));
              const nodesToAdd = newNodes
                .filter((n) => !existingIds.has(n.id))
                .map((n) => ({ ...n, val: n.visual.importance || 1 }));

              const existingEdgeKeys = new Set(
                prev.links.map((l) => edgeKeyFromLink(l))
              );
              const linksToAdd = newEdges
                .filter((e) => !existingEdgeKeys.has(buildEdgeKey(e.source, e.target, e.type)))
                .map((e) => ({ ...e, source: e.source, target: e.target }));

              return {
                nodes: [...prev.nodes, ...nodesToAdd],
                links: [...prev.links, ...linksToAdd],
              };
            });
          } else {
            setError(response.error || 'Error expanding node');
          }
        } catch (err) {
          console.error('Error expanding node:', err);
          setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Error expanding node');
        } finally {
          setExpandingNode(null);
        }
      }

      expansionCoverageRef.current.set(nodeId, {
        nodeIds: coverage.nodeIds,
        edgeKeys: coverage.edgeKeys,
      });

      setExpandedNodes((prev) => {
        const next = new Set(prev);
        next.add(nodeId);
        expandedNodesRef.current = next;
        return next;
      });

      setTimeout(() => {
        if (graphRef.current) {
          graphRef.current.d3ReheatSimulation();
          graphRef.current.zoomToFit(350, 80);
        }
      }, 60);
    },
    [compactMode, getNeighborhoodCoverage, graphData.nodes, pinnedSearchNodeId, selectedNode]
  );

  const handleViewModeToggle = useCallback(() => {
    setCompactMode((prev) => !prev);

    // Global mode switch semantics:
    // - Compact: collapse all
    // - Full: open all
    setExpandedNodes(new Set());
    expandedNodesRef.current = new Set();
    expansionCoverageRef.current = new Map();

    setCollapsedNodes(new Set());
    collapsedNodesRef.current = new Set();
    collapseCoverageRef.current = new Map();

    setHighlightNodes(
      pinnedSearchNodeId ? new Set([pinnedSearchNodeId]) : new Set()
    );
    setHighlightLinks(new Set());
    setError('');
  }, [pinnedSearchNodeId]);

  const handleNodeClick = useCallback(
    (node, event) => {
      const now = Date.now();
      const last = lastNodeClickRef.current;
      const clickDetail = Number(event?.detail || 0);
      const isDoubleFromEvent = clickDetail === 2;
      const isDoubleFromTiming = last.id === node.id && now - last.ts < DOUBLE_CLICK_MS;
      const isDoubleClick = isDoubleFromEvent || isDoubleFromTiming;
      lastNodeClickRef.current = { id: node.id, ts: now };

      if (isDoubleClick) {
        handleNodeExpand(node);
      }

      setSelectedNode(node);
      setNodeDetail(null);
      // Fetch full detail if it's a real requirement
      const nodeDbId = getNodeDbId(node);
      if (nodeDbId) {
        setNodeDetailLoading(true);
        analysisApi.getRequirementDetail(nodeDbId)
          .then((res) => {
            if (res) setNodeDetail(res);
          })
          .catch(() => {})
          .finally(() => setNodeDetailLoading(false));
      }
      // Highlight connected nodes and links
      const connectedNodes = new Set();
      const connectedLinks = new Set();
      connectedNodes.add(node.id);

      graphData.links.forEach((link) => {
        const src = typeof link.source === 'object' ? link.source.id : link.source;
        const tgt = typeof link.target === 'object' ? link.target.id : link.target;
        if (src === node.id || tgt === node.id) {
          connectedNodes.add(src);
          connectedNodes.add(tgt);
          connectedLinks.add(link);
        }
      });

      if (pinnedSearchNodeId) {
        connectedNodes.add(pinnedSearchNodeId);
      }

      setHighlightNodes(connectedNodes);
      setHighlightLinks(connectedLinks);
    },
    [graphData.links, handleNodeExpand, pinnedSearchNodeId]
  );

  const handleBackgroundClick = useCallback(() => {
    setSelectedNode(null);
    setHighlightNodes(
      pinnedSearchNodeId ? new Set([pinnedSearchNodeId]) : new Set()
    );
    setHighlightLinks(new Set());
  }, [pinnedSearchNodeId]);

  const handleZoomIn = () => {
    if (graphRef.current) {
      const currentZoom = graphRef.current.zoom();
      graphRef.current.zoom(currentZoom * 1.5, 300);
    }
  };

  const handleZoomOut = () => {
    if (graphRef.current) {
      const currentZoom = graphRef.current.zoom();
      graphRef.current.zoom(currentZoom / 1.5, 300);
    }
  };

  const handleCenterGraph = () => {
    if (graphRef.current) {
      graphRef.current.zoomToFit(400, 50);
    }
  };

  const searchOptions = useMemo(() => {
    const options = graphData.nodes.map((node) => {
      const notation = node.notation || '';
      const labelOriginal = node.label_original || node.label || '';
      const labelEnglish = node.label_english || '';
      const snippet = (node.has_requirement || '').slice(0, 120);
      const primaryText = labelEnglish || labelOriginal || 'Node';
      const primaryLabel = notation
        ? `${notation} - ${primaryText}`
        : primaryText;
      const secondaryLabel = labelEnglish && labelOriginal && labelEnglish !== labelOriginal
        ? `NL: ${labelOriginal}`
        : snippet;

      return {
        id: node.id,
        primaryLabel,
        secondaryLabel,
        notationLower: notation.toLowerCase(),
        originalLower: labelOriginal.toLowerCase(),
        englishLower: labelEnglish.toLowerCase(),
        snippetLower: snippet.toLowerCase(),
      };
    });

    options.sort((a, b) => a.primaryLabel.localeCompare(b.primaryLabel));
    return options;
  }, [graphData.nodes]);

  // Filtered graph data
  const filteredGraphData = useMemo(() => {
    let nodes = [...graphData.nodes];
    let links = [...graphData.links];
    const getNodeId = (endpoint) => (typeof endpoint === 'object' ? endpoint.id : endpoint);

    // In compact mode, only explicitly expanded node families are shown.
    const expandedFamilyIds = new Set();
    expandedNodes.forEach((expandedNodeId) => {
      expandedFamilyIds.add(expandedNodeId);
      const coverage = expansionCoverageRef.current.get(expandedNodeId);
      if (coverage?.nodeIds) {
        coverage.nodeIds.forEach((id) => expandedFamilyIds.add(id));
      }
    });

    // Relationship type filter
    if (activeFilters.relationship_type.length > 0) {
      links = links.filter((l) =>
        activeFilters.relationship_type.includes(normalizeRelationshipType(l.type))
      );
      const connectedIds = new Set();
      links.forEach((l) => {
          const src = getNodeId(l.source);
          const tgt = getNodeId(l.target);
        connectedIds.add(src);
        connectedIds.add(tgt);
      });
      nodes = nodes.filter((n) => connectedIds.has(n.id));
    }

    // SMART filter
    if (activeFilters.smart_compliant !== null) {
      nodes = nodes.filter((n) => n.is_smart_compliant === activeFilters.smart_compliant);
      const nodeIds = new Set(nodes.map((n) => n.id));
      links = links.filter((l) => {
          const src = getNodeId(l.source);
          const tgt = getNodeId(l.target);
        return nodeIds.has(src) && nodeIds.has(tgt);
      });
    }

    // Translation filter
    if (activeFilters.translation_status !== null) {
      const isCompleted = activeFilters.translation_status === 'completed';
      nodes = nodes.filter((n) => n.translation_completed === isCompleted);
      const nodeIds = new Set(nodes.map((n) => n.id));
      links = links.filter((l) => {
          const src = getNodeId(l.source);
          const tgt = getNodeId(l.target);
        return nodeIds.has(src) && nodeIds.has(tgt);
      });
    }

    // Validation filter
    if (activeFilters.validation_status !== null) {
      const isValidated = activeFilters.validation_status === 'validated';
      nodes = nodes.filter((n) => Boolean(n.is_validated) === isValidated);
      const nodeIds = new Set(nodes.map((n) => n.id));
      links = links.filter((l) => {
          const src = getNodeId(l.source);
          const tgt = getNodeId(l.target);
        return nodeIds.has(src) && nodeIds.has(tgt);
      });
    }

    // Compact view: collapsed by default, with optional per-node family expansion.
    if (compactMode) {
      const blueIds = new Set(
        nodes
          .filter((n) => (n?.visual?.color || '').toLowerCase() === COMPACT_BLUE_COLOR)
          .map((n) => n.id)
      );

      const visibleIds = new Set([...blueIds, ...expandedFamilyIds]);

      nodes = nodes.filter((n) => visibleIds.has(n.id));
      links = links.filter((l) => {
        const src = getNodeId(l.source);
        const tgt = getNodeId(l.target);
        return visibleIds.has(src) && visibleIds.has(tgt);
      });
    } else if (collapsedNodes.size > 0) {
      // Full view: open by default; only nodes manually collapsed via double click are hidden.
      const hiddenIds = new Set();
      const hiddenEdgeKeys = new Set();

      collapsedNodes.forEach((collapsedNodeId) => {
        const coverage = collapseCoverageRef.current.get(collapsedNodeId);
        if (!coverage) return;

        coverage.nodeIds?.forEach((id) => {
          if (id !== collapsedNodeId) hiddenIds.add(id);
        });
        coverage.edgeKeys?.forEach((key) => hiddenEdgeKeys.add(key));
      });

      nodes = nodes.filter((n) => !hiddenIds.has(n.id));
      links = links.filter((l) => {
        const src = getNodeId(l.source);
        const tgt = getNodeId(l.target);
        const key = buildEdgeKey(src, tgt, l.type);
        return !hiddenIds.has(src) && !hiddenIds.has(tgt) && !hiddenEdgeKeys.has(key);
      });
    }

    return { nodes, links };
    }, [graphData, activeFilters, compactMode, expandedNodes, collapsedNodes]);

  const hierarchicalPositions = useMemo(() => {
    if (activeLayout !== 'hierarchical') {
      return new Map();
    }
    // Keep layout anchored to the full loaded graph so collapsing/expanding visibility
    // does not reflow positions of the remaining visible nodes.
    return computeHierarchicalPositions(graphData.nodes, graphData.links);
  }, [activeLayout, graphData.nodes, graphData.links]);

  const filteredFallbackPositions = useMemo(
    () => computeHierarchicalPositions(filteredGraphData.nodes, filteredGraphData.links),
    [filteredGraphData.nodes, filteredGraphData.links]
  );

  const fullFallbackPositions = useMemo(
    () => computeHierarchicalPositions(graphData.nodes, graphData.links),
    [graphData.nodes, graphData.links]
  );

  const filteredForceClusterPositions = useMemo(
    () => computeClusterPositions(filteredGraphData.nodes, filteredGraphData.links),
    [filteredGraphData.nodes, filteredGraphData.links]
  );

  useEffect(() => {
    if (activeLayout !== 'hierarchical' || hierarchicalPositions.size === 0) {
      return;
    }

    filteredGraphData.nodes.forEach((node) => {
      const pos = hierarchicalPositions.get(node.id);
      if (!pos) return;
      node.x = pos.x;
      node.y = pos.y;
      node.fx = pos.x;
      node.fy = pos.y;
      node.vx = 0;
      node.vy = 0;
    });

    const t = setTimeout(() => {
      graphRef.current?.d3ReheatSimulation();
    }, 20);

    return () => clearTimeout(t);
  }, [activeLayout, filteredGraphData.nodes, hierarchicalPositions]);

  const orderedSubgraphs = useMemo(() => {
    const nodes = filteredGraphData.nodes;
    const links = filteredGraphData.links;
    if (!nodes.length) return [];

    const nodeMap = new Map(nodes.map((node) => [node.id, node]));
    const adjacency = new Map(nodes.map((node) => [node.id, new Set()]));

    links.forEach((link) => {
      const src = getEndpointId(link.source);
      const tgt = getEndpointId(link.target);
      if (!src || !tgt || !adjacency.has(src) || !adjacency.has(tgt)) return;
      adjacency.get(src).add(tgt);
      adjacency.get(tgt).add(src);
    });

    const visited = new Set();
    const components = [];

    nodeMap.forEach((_node, nodeId) => {
      if (visited.has(nodeId)) return;

      const queue = [nodeId];
      const componentIds = [];
      visited.add(nodeId);

      while (queue.length) {
        const current = queue.shift();
        componentIds.push(current);
        (adjacency.get(current) || new Set()).forEach((neighborId) => {
          if (visited.has(neighborId)) return;
          visited.add(neighborId);
          queue.push(neighborId);
        });
      }

      const positioned = componentIds
        .map((id) => {
          const node = nodeMap.get(id);
          if (!node) return null;

          if (activeLayout === 'hierarchical') {
            const pos = hierarchicalPositions.get(id);
            if (pos) return { id, x: pos.x, y: pos.y };
          }

          if (Number.isFinite(node.x) && Number.isFinite(node.y)) {
            return { id, x: node.x, y: node.y };
          }

          if (activeLayout === 'force-directed') {
            const forcePos = filteredForceClusterPositions.get(id);
            if (forcePos) return { id, x: forcePos.x, y: forcePos.y };
          }

          const fallbackPos = filteredFallbackPositions.get(id);
          if (fallbackPos) {
            return { id, x: fallbackPos.x, y: fallbackPos.y };
          }

          return null;
        })
        .filter(Boolean);

      let minX = 0;
      let maxX = 0;
      let minY = 0;
      let maxY = 0;

      if (positioned.length) {
        const xs = positioned.map((p) => p.x);
        const ys = positioned.map((p) => p.y);
        minX = Math.min(...xs);
        maxX = Math.max(...xs);
        minY = Math.min(...ys);
        maxY = Math.max(...ys);
      }

      const sortKey = [...componentIds]
        .map((id) => {
          const node = nodeMap.get(id);
          return String(node?.notation || node?.label_english || node?.label_original || node?.label || id);
        })
        .sort((a, b) => a.localeCompare(b))[0] || String(componentIds[0] || '');

      components.push({
        nodeIds: new Set(componentIds),
        sortKey,
        minX,
        maxX,
        minY,
        maxY,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2,
      });
    });

    return components.sort((a, b) => {
      return a.sortKey.localeCompare(b.sortKey);
    });
  }, [activeLayout, filteredGraphData.links, filteredGraphData.nodes, hierarchicalPositions, filteredFallbackPositions, filteredForceClusterPositions]);

  const fullOrderedSubgraphs = useMemo(() => {
    const nodes = graphData.nodes;
    const links = graphData.links;
    if (!nodes.length) return [];

    const nodeMap = new Map(nodes.map((node) => [node.id, node]));
    const adjacency = new Map(nodes.map((node) => [node.id, new Set()]));

    links.forEach((link) => {
      const src = getEndpointId(link.source);
      const tgt = getEndpointId(link.target);
      if (!src || !tgt || !adjacency.has(src) || !adjacency.has(tgt)) return;
      adjacency.get(src).add(tgt);
      adjacency.get(tgt).add(src);
    });

    const visited = new Set();
    const components = [];

    nodeMap.forEach((_node, nodeId) => {
      if (visited.has(nodeId)) return;

      const queue = [nodeId];
      const componentIds = [];
      visited.add(nodeId);

      while (queue.length) {
        const current = queue.shift();
        componentIds.push(current);
        (adjacency.get(current) || new Set()).forEach((neighborId) => {
          if (visited.has(neighborId)) return;
          visited.add(neighborId);
          queue.push(neighborId);
        });
      }

      const positioned = componentIds
        .map((id) => {
          const node = nodeMap.get(id);
          if (!node) return null;

          if (activeLayout === 'hierarchical') {
            const pos = hierarchicalPositions.get(id);
            if (pos) return { id, x: pos.x, y: pos.y };
          }

          if (Number.isFinite(node.x) && Number.isFinite(node.y)) {
            return { id, x: node.x, y: node.y };
          }

          if (activeLayout === 'force-directed') {
            const forcePos = forceClusterPositions.get(id);
            if (forcePos) return { id, x: forcePos.x, y: forcePos.y };
          }

          const fallbackPos = fullFallbackPositions.get(id);
          if (fallbackPos) {
            return { id, x: fallbackPos.x, y: fallbackPos.y };
          }

          return null;
        })
        .filter(Boolean);

      let minX = 0;
      let maxX = 0;
      let minY = 0;
      let maxY = 0;

      if (positioned.length) {
        const xs = positioned.map((p) => p.x);
        const ys = positioned.map((p) => p.y);
        minX = Math.min(...xs);
        maxX = Math.max(...xs);
        minY = Math.min(...ys);
        maxY = Math.max(...ys);
      }

      const sortKey = [...componentIds]
        .map((id) => {
          const node = nodeMap.get(id);
          return String(node?.notation || node?.label_english || node?.label_original || node?.label || id);
        })
        .sort((a, b) => a.localeCompare(b))[0] || String(componentIds[0] || '');

      components.push({
        nodeIds: new Set(componentIds),
        sortKey,
        minX,
        maxX,
        minY,
        maxY,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2,
      });
    });

    return components.sort((a, b) => {
      return a.sortKey.localeCompare(b.sortKey);
    });
  }, [activeLayout, graphData.links, graphData.nodes, hierarchicalPositions, fullFallbackPositions, forceClusterPositions]);

  const liveNodeById = useMemo(() => {
    const map = new Map();
    graphData.nodes.forEach((node) => map.set(node.id, node));
    filteredGraphData.nodes.forEach((node) => map.set(node.id, node));
    return map;
  }, [graphData.nodes, filteredGraphData.nodes]);

  const getLiveSubgraphBounds = useCallback(
    (nodeIds) => {
      if (!nodeIds || nodeIds.size === 0) return null;

      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      let positionedCount = 0;

      nodeIds.forEach((id) => {
        const node = liveNodeById.get(id);
        if (!node || !Number.isFinite(node.x) || !Number.isFinite(node.y)) return;

        if (activeLayout === 'force-directed') {
          const seededPos = forceClusterPositions.get(id);
          if (seededPos) {
            const dx = Math.abs(node.x - seededPos.x);
            const dy = Math.abs(node.y - seededPos.y);
            // Ignore untouched seed coordinates so centering uses real simulated positions.
            if (dx < 0.75 && dy < 0.75) return;
          }
        }

        positionedCount += 1;
        minX = Math.min(minX, node.x);
        maxX = Math.max(maxX, node.x);
        minY = Math.min(minY, node.y);
        maxY = Math.max(maxY, node.y);
      });

      if (positionedCount === 0) return null;

      if (activeLayout === 'force-directed') {
        const requiredPoints = nodeIds.size <= 1
          ? 1
          : Math.max(2, Math.ceil(nodeIds.size * 0.35));
        if (positionedCount < requiredPoints) return null;
      }

      return {
        nodeIds: new Set(nodeIds),
        minX,
        maxX,
        minY,
        maxY,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2,
      };
    },
    [activeLayout, forceClusterPositions, liveNodeById]
  );

  const centerOnSubgraph = useCallback(
    (subgraph, animateMs = 500) => {
      if (!graphRef.current || !subgraph) return false;

      const liveSubgraph = subgraph?.nodeIds ? getLiveSubgraphBounds(subgraph.nodeIds) : null;
      const targetSubgraph = liveSubgraph || (activeLayout === 'force-directed' ? null : subgraph);
      if (!targetSubgraph) return false;

      const viewportWidth = Math.max(1, graphDimensions.width || graphContainerRef.current?.clientWidth || 1200);
      const viewportHeight = Math.max(1, graphDimensions.height || graphContainerRef.current?.clientHeight || 800);

      const bboxWidth = Math.max(180, targetSubgraph.maxX - targetSubgraph.minX + 260);
      const bboxHeight = Math.max(180, targetSubgraph.maxY - targetSubgraph.minY + 240);

      let zoomTarget = Math.min(viewportWidth / bboxWidth, viewportHeight / bboxHeight);
      const minZoom = activeLayout === 'hierarchical' ? 0.7 : 0.12;
      const maxZoom = activeLayout === 'hierarchical' ? 3.2 : 2.8;
      zoomTarget = Math.max(minZoom, Math.min(zoomTarget, maxZoom));

      graphRef.current.centerAt(targetSubgraph.centerX, targetSubgraph.centerY, animateMs);
      graphRef.current.zoom(zoomTarget, animateMs);
      if (subgraph?.nodeIds) {
        activeSubgraphNodeIdsRef.current = new Set(subgraph.nodeIds);
      }
      return true;
    },
    [activeLayout, getLiveSubgraphBounds, graphDimensions.height, graphDimensions.width]
  );

  const centerOnSubgraphWithRetry = useCallback(
    (subgraph, animateMs = 500, options = {}) => {
      const retries = options.retries ?? 8;
      const delayMs = options.delayMs ?? 90;
      const onCentered = options.onCentered;

      let attempts = 0;
      let retryTimer;

      const tryCenter = () => {
        const centered = centerOnSubgraph(subgraph, animateMs);
        if (centered) {
          if (onCentered) onCentered();
          return;
        }

        if (attempts >= retries) return;
        attempts += 1;
        retryTimer = setTimeout(tryCenter, delayMs);
      };

      tryCenter();

      return () => {
        if (retryTimer) clearTimeout(retryTimer);
      };
    },
    [centerOnSubgraph]
  );

  useEffect(() => {
    if (previousLayoutRef.current === null) {
      previousLayoutRef.current = activeLayout;
      return;
    }

    const layoutChanged = previousLayoutRef.current !== activeLayout;
    previousLayoutRef.current = activeLayout;

    if (!layoutChanged || !orderedSubgraphs.length) {
      return;
    }

    let targetIndex = Math.min(currentSubgraphIndex, orderedSubgraphs.length - 1);
    const originalNodeIds = originalSubgraphNodeIdsRef.current;
    const selectedNodeIds = activeSubgraphNodeIdsRef.current;

    if (originalNodeIds.size > 0) {
      let bestIndex = -1;
      let bestOverlap = 0;

      orderedSubgraphs.forEach((subgraph, index) => {
        let overlap = 0;
        subgraph.nodeIds.forEach((id) => {
          if (originalNodeIds.has(id)) overlap += 1;
        });

        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          bestIndex = index;
        }
      });

      if (bestIndex >= 0) {
        targetIndex = bestIndex;
      }
    } else if (selectedNodeIds.size > 0) {
      let bestIndex = -1;
      let bestOverlap = 0;

      orderedSubgraphs.forEach((subgraph, index) => {
        let overlap = 0;
        subgraph.nodeIds.forEach((id) => {
          if (selectedNodeIds.has(id)) overlap += 1;
        });

        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          bestIndex = index;
        }
      });

      if (bestIndex >= 0) {
        targetIndex = bestIndex;
      }
    } else if (selectedNode?.id) {
      const selectedNodeSubgraphIndex = orderedSubgraphs.findIndex((subgraph) =>
        subgraph.nodeIds.has(selectedNode.id)
      );
      if (selectedNodeSubgraphIndex >= 0) {
        targetIndex = selectedNodeSubgraphIndex;
      }
    } else if (originalFocusNodeIdRef.current) {
      const originalFocusSubgraphIndex = orderedSubgraphs.findIndex((subgraph) =>
        subgraph.nodeIds.has(originalFocusNodeIdRef.current)
      );
      if (originalFocusSubgraphIndex >= 0) {
        targetIndex = originalFocusSubgraphIndex;
      }
    }

    let cancelRetry;
    const t = setTimeout(() => {
      cancelRetry = centerOnSubgraphWithRetry(
        orderedSubgraphs[targetIndex],
        activeLayout === 'hierarchical' ? 450 : 520,
        {
          retries: activeLayout === 'hierarchical' ? 3 : 24,
          delayMs: activeLayout === 'hierarchical' ? 70 : 120,
          onCentered: () => setCurrentSubgraphIndex(targetIndex),
        }
      );
    }, activeLayout === 'hierarchical' ? 90 : 160);

    return () => {
      clearTimeout(t);
      if (cancelRetry) cancelRetry();
    };
  }, [activeLayout, centerOnSubgraphWithRetry, currentSubgraphIndex, orderedSubgraphs, selectedNode]);

  const centerOnFocusSubgraph = useCallback(
    (animateMs = 500) => {
      if (!graphView?.focusNode || !orderedSubgraphs.length) return false;

      const focusIndex = orderedSubgraphs.findIndex((subgraph) =>
        subgraph.nodeIds.has(graphView.focusNode)
      );

      const targetIndex = focusIndex >= 0 ? focusIndex : 0;
      const centered = centerOnSubgraph(orderedSubgraphs[targetIndex], animateMs);
      if (centered) {
        setCurrentSubgraphIndex(targetIndex);
      }
      return centered;
    },
    [centerOnSubgraph, graphView?.focusNode, orderedSubgraphs]
  );

  const handleSearchSelect = useCallback(
    (option) => {
      if (!option?.id) return;

      const node = graphData.nodes.find((n) => n.id === option.id);
      if (!node) return;

      const focusTerm = node.label_english || node.label_original || node.label || node.notation || '';
      setSearchTerm(focusTerm);
      setSelectedNode(node);
      setNodeDetail(null);
      setPinnedSearchNodeId(node.id);

      setHighlightNodes(new Set([node.id]));
      setHighlightLinks(new Set());

      const subgraphIndex = fullOrderedSubgraphs.findIndex((subgraph) => subgraph.nodeIds.has(node.id));
      if (subgraphIndex >= 0) {
        centerOnSubgraph(fullOrderedSubgraphs[subgraphIndex], 500);
        setCurrentSubgraphIndex(subgraphIndex);
      } else if (graphRef.current && Number.isFinite(node.x) && Number.isFinite(node.y)) {
        graphRef.current.centerAt(node.x, node.y, 500);
        graphRef.current.zoom(2.6, 500);
      }
    },
    [centerOnSubgraph, fullOrderedSubgraphs, graphData.nodes]
  );

  const handleCenterPreviousSubgraph = useCallback(() => {
    if (!orderedSubgraphs.length) return;

    setCurrentSubgraphIndex((prev) => {
      const nextIndex = prev <= 0 ? orderedSubgraphs.length - 1 : prev - 1;
      centerOnSubgraphWithRetry(orderedSubgraphs[nextIndex], 450, {
        retries: activeLayout === 'hierarchical' ? 2 : 6,
        delayMs: 70,
      });
      return nextIndex;
    });
  }, [activeLayout, centerOnSubgraphWithRetry, orderedSubgraphs]);

  const handleCenterNextSubgraph = useCallback(() => {
    if (!orderedSubgraphs.length) return;

    setCurrentSubgraphIndex((prev) => {
      const nextIndex = prev >= orderedSubgraphs.length - 1 ? 0 : prev + 1;
      centerOnSubgraphWithRetry(orderedSubgraphs[nextIndex], 450, {
        retries: activeLayout === 'hierarchical' ? 2 : 6,
        delayMs: 70,
      });
      return nextIndex;
    });
  }, [activeLayout, centerOnSubgraphWithRetry, orderedSubgraphs]);

  const handleCenterOnFocus = () => {
    centerOnFocusSubgraph(500);
  };

  useEffect(() => {
    if (!orderedSubgraphs.length) {
      activeSubgraphNodeIdsRef.current = new Set();
      if (currentSubgraphIndex !== 0) {
        setCurrentSubgraphIndex(0);
      }
      return;
    }

    if (currentSubgraphIndex > orderedSubgraphs.length - 1) {
      setCurrentSubgraphIndex(orderedSubgraphs.length - 1);
      return;
    }

    const activeSubgraph = orderedSubgraphs[currentSubgraphIndex];
    if (activeSubgraph?.nodeIds) {
      activeSubgraphNodeIdsRef.current = new Set(activeSubgraph.nodeIds);
    }
  }, [currentSubgraphIndex, orderedSubgraphs]);

  useEffect(() => {
    if (!open || !graphView?.focusNode || hasInitialFocusCenteredRef.current) return;

    let cancelRetry;
    const t = setTimeout(() => {
      const focusIndex = orderedSubgraphs.findIndex((subgraph) =>
        subgraph.nodeIds.has(graphView.focusNode)
      );

      const targetIndex = focusIndex >= 0 ? focusIndex : 0;
      cancelRetry = centerOnSubgraphWithRetry(
        orderedSubgraphs[targetIndex],
        550,
        {
          retries: activeLayout === 'hierarchical' ? 6 : 24,
          delayMs: activeLayout === 'hierarchical' ? 60 : 120,
          onCentered: () => {
            setCurrentSubgraphIndex(targetIndex);
            originalSubgraphNodeIdsRef.current = new Set(orderedSubgraphs[targetIndex]?.nodeIds || [graphView.focusNode]);
            originalFocusNodeIdRef.current = graphView.focusNode;
            hasInitialFocusCenteredRef.current = true;
          },
        }
      );
    }, activeLayout === 'hierarchical' ? 50 : 220);

    return () => {
      clearTimeout(t);
      if (cancelRetry) cancelRetry();
    };
  }, [activeLayout, centerOnSubgraphWithRetry, graphView?.focusNode, open, orderedSubgraphs]);

  // Node canvas rendering — improved for expert readability
  const nodeCanvasObject = useCallback(
    (node, ctx, globalScale) => {
      const isHighlighted = highlightNodes.has(node.id);
      const isFocus = node.id === graphView?.focusNode;
      const isExpanding = expandingNode === node.id;
      const isExternal = node.visual?.shape === 'diamond' || node.nodeType === 'external' || node.type === 'document' || node.type === 'verificationplan' || node.type === 'specification';
      const isCrossFile = node.cross_file === true;

      const baseSize = isFocus ? 16 : node.visual?.size === 'large' ? 13 : node.visual?.size === 'medium' ? 10 : 8;
      const size = isHighlighted ? baseSize * 1.25 : baseSize;
      const color = node.visual?.color || '#2196F3';

      // Shadow / glow for highlighted nodes
      if (isHighlighted || isFocus) {
        ctx.shadowColor = isFocus ? '#FFD700' : color;
        ctx.shadowBlur = isFocus ? 18 : 10;
      }

      // Draw shape based on node type
      if (isExternal) {
        // Rounded square for external document references
        const r = size * 0.35;
        const x = node.x - size;
        const y = node.y - size;
        const w = size * 2;
        const h = size * 2;
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
        ctx.fillStyle = isHighlighted ? color : `${color}CC`;
        ctx.fill();
        ctx.strokeStyle = isHighlighted ? '#fff' : `${color}FF`;
        ctx.lineWidth = isHighlighted ? 2 : 1.5;
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.fillRect(node.x - size * 0.4, node.y - size * 0.5, size * 0.8, size * 0.25);
        ctx.fillRect(node.x - size * 0.4, node.y - size * 0.1, size * 0.8, size * 0.25);
      } else if (isCrossFile) {
        // Diamond shape for cross-file requirements
        ctx.beginPath();
        ctx.moveTo(node.x, node.y - size * 1.3);
        ctx.lineTo(node.x + size * 1.1, node.y);
        ctx.lineTo(node.x, node.y + size * 1.3);
        ctx.lineTo(node.x - size * 1.1, node.y);
        ctx.closePath();
        ctx.fillStyle = isHighlighted ? color : `${color}CC`;
        ctx.fill();
        ctx.strokeStyle = isHighlighted ? '#fff' : 'rgba(255,255,255,0.7)';
        ctx.lineWidth = isHighlighted ? 2.5 : 1.5;
        ctx.stroke();
        ctx.shadowBlur = 0;
        // Dashed border to signal "external origin"
        ctx.setLineDash([3, 2]);
        ctx.beginPath();
        ctx.moveTo(node.x, node.y - size * 1.5);
        ctx.lineTo(node.x + size * 1.3, node.y);
        ctx.lineTo(node.x, node.y + size * 1.5);
        ctx.lineTo(node.x - size * 1.3, node.y);
        ctx.closePath();
        ctx.strokeStyle = `${color}88`;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);
      } else {
        // Circle for same-file requirements
        ctx.beginPath();
        ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
        ctx.fillStyle = isHighlighted ? color : `${color}DD`;
        ctx.fill();
        ctx.strokeStyle = isHighlighted ? '#fff' : 'rgba(255,255,255,0.5)';
        ctx.lineWidth = isHighlighted ? 2.5 : 1.5;
        ctx.stroke();
      }

      ctx.shadowBlur = 0;

      // Focus node: prominent golden ring
      if (isFocus) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, size + 5, 0, 2 * Math.PI);
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 3.5;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(node.x, node.y, size + 9, 0, 2 * Math.PI);
        ctx.strokeStyle = 'rgba(255, 215, 0, 0.25)';
        ctx.lineWidth = 4;
        ctx.stroke();
      } else if (isHighlighted) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, size + 4, 0, 2 * Math.PI);
        ctx.strokeStyle = `${color}55`;
        ctx.lineWidth = 3;
        ctx.stroke();
      }

      // Expanding indicator
      if (isExpanding) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, size + 6, 0, 2 * Math.PI);
        ctx.strokeStyle = '#FF5722';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Label — always visible, word-wrapped
      const rawLabel =
        labelMode === 'english'
          ? (node.label_english || node.label_original || node.notation || node.label || '')
          : labelMode === 'notation'
          ? (node.notation || node.label || '')
          : (node.label_original || node.label || node.notation || '');
      const fontSize = Math.max(10 / globalScale, isFocus ? 5 : 4);
      const fontWeight = isFocus ? 'bold' : isHighlighted ? '600' : '500';
      ctx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      // Word-wrap: split into lines of max width, wider for external nodes
      const maxLineWidth = isExternal ? Math.max(120, size * 12) : Math.max(80, size * 8);
      const words = rawLabel.split(' ');
      const lines = [];
      let current = '';
      for (const word of words) {
        const test = current ? `${current} ${word}` : word;
        if (ctx.measureText(test).width > maxLineWidth && current) {
          lines.push(current);
          current = word;
        } else {
          current = test;
        }
      }
      if (current) lines.push(current);
      // Cap at 3 lines max
      if (lines.length > 3) {
        lines.splice(3);
        lines[2] = lines[2].slice(0, -1) + '…';
      }

      const lineH = fontSize + 1;
      const boxW = Math.max(...lines.map((l) => ctx.measureText(l).width));
      const labelY = node.y + size + 4;
      const padX = 4;
      const padY = 2;
      const bRadius = 3;
      const bx = node.x - boxW / 2 - padX;
      const by = labelY - padY;
      const bw = boxW + padX * 2;
      const bh = lines.length * lineH + padY * 2;

      // Rounded pill background
      ctx.fillStyle = isFocus ? 'rgba(255,248,200,0.95)' : 'rgba(255,255,255,0.92)';
      ctx.beginPath();
      ctx.moveTo(bx + bRadius, by);
      ctx.lineTo(bx + bw - bRadius, by);
      ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + bRadius);
      ctx.lineTo(bx + bw, by + bh - bRadius);
      ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - bRadius, by + bh);
      ctx.lineTo(bx + bRadius, by + bh);
      ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - bRadius);
      ctx.lineTo(bx, by + bRadius);
      ctx.quadraticCurveTo(bx, by, bx + bRadius, by);
      ctx.closePath();
      ctx.fill();
      if (isFocus) {
        ctx.strokeStyle = 'rgba(200,160,0,0.5)';
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      ctx.fillStyle = isFocus ? '#7a5900' : isHighlighted ? '#111' : '#333';
      lines.forEach((line, i) => {
        ctx.fillText(line, node.x, labelY + i * lineH);
      });
    },
    [highlightNodes, graphView, expandingNode, labelMode]
  );

  // Link canvas rendering — always show relationship labels
  const linkCanvasObject = useCallback(
    (link, ctx, globalScale) => {
      const isHighlighted = highlightLinks.has(link);
      const color = EDGE_COLORS[link.type] || '#999';
      const width = isHighlighted ? 3 : 1.8;

      const src = link.source;
      const tgt = link.target;
      if (!src || !tgt || typeof src.x === 'undefined') return;

      const dx = tgt.x - src.x;
      const dy = tgt.y - src.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;

      // Perpendicular offset for curve (proportional to distance)
      const curvature = 0.2;
      const cpX = (src.x + tgt.x) / 2 - curvature * dy;
      const cpY = (src.y + tgt.y) / 2 + curvature * dx;

      // Draw curved edge
      ctx.beginPath();
      ctx.moveTo(src.x, src.y);
      ctx.quadraticCurveTo(cpX, cpY, tgt.x, tgt.y);
      ctx.strokeStyle = isHighlighted ? color : `${color}99`;
      ctx.lineWidth = width;
      if (!isHighlighted) ctx.setLineDash([]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Arrow at ~80% of the curve toward target
      const t = 0.78;
      const arrowX = (1 - t) * (1 - t) * src.x + 2 * (1 - t) * t * cpX + t * t * tgt.x;
      const arrowY = (1 - t) * (1 - t) * src.y + 2 * (1 - t) * t * cpY + t * t * tgt.y;
      // Tangent at t
      const tanX = 2 * (1 - t) * (cpX - src.x) + 2 * t * (tgt.x - cpX);
      const tanY = 2 * (1 - t) * (cpY - src.y) + 2 * t * (tgt.y - cpY);
      const angle = Math.atan2(tanY, tanX);
      const arrowLen = isHighlighted ? 10 : 7;

      ctx.beginPath();
      ctx.moveTo(arrowX, arrowY);
      ctx.lineTo(
        arrowX - arrowLen * Math.cos(angle - Math.PI / 6),
        arrowY - arrowLen * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        arrowX - arrowLen * Math.cos(angle + Math.PI / 6),
        arrowY - arrowLen * Math.sin(angle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fillStyle = isHighlighted ? color : `${color}AA`;
      ctx.fill();

      // Edge label — only show when zoomed in enough or highlighted
      const label = EDGE_LABELS[link.type] || link.type;
      // Midpoint of bezier at t=0.5, offset perpendicular to pull label off the line
      const midX = (src.x + tgt.x) / 2 - curvature * dy * 0.5;
      const midY = (src.y + tgt.y) / 2 + curvature * dx * 0.5;
      // Perpendicular offset: push label away from the curve
      const perpLen = dist || 1;
      const perpOffsetDist = 10 / globalScale;
      const labelX = midX - (dy / perpLen) * perpOffsetDist;
      const labelY = midY + (dx / perpLen) * perpOffsetDist;
      const minScale = isHighlighted ? 0 : 0.6;
      if (globalScale < minScale) return;

      const fontSize = Math.max(9 / globalScale, 4);
      ctx.font = `${isHighlighted ? '700' : '500'} ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const textWidth = ctx.measureText(label).width;
      const padX = 3;
      const padY = 1.5;
      // Pill background
      ctx.fillStyle = isHighlighted ? `${color}22` : 'rgba(255,255,255,0.88)';
      const rx = labelX - textWidth / 2 - padX;
      const ry = labelY - fontSize / 2 - padY;
      const rw = textWidth + padX * 2;
      const rh = fontSize + padY * 2;
      ctx.beginPath();
      ctx.roundRect(rx, ry, rw, rh, 3);
      ctx.fill();
      if (isHighlighted) {
        ctx.strokeStyle = `${color}66`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      ctx.fillStyle = isHighlighted ? color : `${color}CC`;
      ctx.fillText(label, labelX, labelY);
    },
    [highlightLinks]
  );

  // Layout config based on active layout (2 professional modes)
  const layoutConfig = useMemo(() => {
    if (activeLayout === 'hierarchical') {
      return { dagMode: null };
    }
    return { dagMode: null };
  }, [activeLayout]);

  const isHierarchicalLayout = activeLayout === 'hierarchical';
  const warmupTicks = isHierarchicalLayout ? 40 : 100;
  const alphaDecay = isHierarchicalLayout ? 0.12 : 0.008;
  const velocityDecay = isHierarchicalLayout ? 0.8 : 0.3;

  const graphStats = useMemo(() => {
    const isExternalNode = (n) => n?.cross_file === true || n?.type !== 'requirement';

    const visibleExternal = filteredGraphData.nodes.filter(isExternalNode).length;
    const visibleFileRequirements = filteredGraphData.nodes.length - visibleExternal;

    const loadedExternal = graphData.nodes.filter(isExternalNode).length;
    const loadedFileRequirements = graphData.nodes.length - loadedExternal;

    return {
      visibleExternal,
      visibleFileRequirements,
      loadedExternal,
      loadedFileRequirements,
    };
  }, [filteredGraphData.nodes, graphData.nodes]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: '#f0f4f8',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          px: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'white',
        }}
      >
        <Hub sx={{ color: 'primary.main' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Graph View — {fileName || 'Requirements'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {graphView
                  ? `${filteredGraphData.nodes.length} visible (${graphStats.visibleFileRequirements} in-file + ${graphStats.visibleExternal} external) · ${filteredGraphData.links.length} edges · ${graphView.totalNodeCount} total requirements in file`
              : 'Loading...'}
          </Typography>
        </Box>

            <Tooltip title={compactMode ? 'Compact view enabled (main nodes)' : 'Showing all loaded nodes'}>
            <Chip
              size="small"
              color={compactMode ? 'primary' : 'default'}
              variant={compactMode ? 'filled' : 'outlined'}
              label={compactMode ? 'Compact View' : 'Full View'}
              onClick={handleViewModeToggle}
            />
          </Tooltip>

        {/* Layout selector */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Layout</InputLabel>
          <Select
            value={activeLayout}
            label="Layout"
            onChange={(e) => {
              const activeSubgraph = orderedSubgraphs[currentSubgraphIndex];
              if (activeSubgraph?.nodeIds) {
                activeSubgraphNodeIdsRef.current = new Set(activeSubgraph.nodeIds);
              }
              setActiveLayout(e.target.value);
            }}
          >
            <MenuItem value="hierarchical">Hierarchical (Traceability)</MenuItem>
            <MenuItem value="force-directed">Force-Directed (Clusters)</MenuItem>
          </Select>
        </FormControl>

        {/* Depth selector */}
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Depth</InputLabel>
          <Select
            value={graphDepth}
            label="Depth"
            onChange={(e) => setGraphDepth(e.target.value)}
          >
            <MenuItem value={1}>1 — Direct</MenuItem>
            <MenuItem value={2}>2 — Extended</MenuItem>
            <MenuItem value={3}>3 — Full</MenuItem>
          </Select>
        </FormControl>

        {/* Search */}
        <Autocomplete
          size="small"
          sx={{ width: 300 }}
          options={searchOptions}
          value={searchSelection}
          inputValue={searchTerm}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          getOptionLabel={(option) => option?.primaryLabel || ''}
          filterOptions={(options, state) => {
            const term = state.inputValue.trim().toLowerCase();
            if (!term) return options.slice(0, 12);

            return options
              .filter((option) =>
                option.notationLower.includes(term) ||
                option.originalLower.includes(term) ||
                option.englishLower.includes(term) ||
                option.snippetLower.includes(term)
              )
              .slice(0, 20);
          }}
          noOptionsText="No matching nodes"
          onInputChange={(_event, newInputValue, reason) => {
            if (reason === 'clear') {
              setSearchTerm('');
              setSearchSelection(null);
              setPinnedSearchNodeId(null);
              setHighlightNodes(new Set());
              setHighlightLinks(new Set());
              return;
            }
            setSearchTerm(newInputValue);
          }}
          onChange={(_event, newValue) => {
            setSearchSelection(newValue);
            if (!newValue) {
              setPinnedSearchNodeId(null);
              setHighlightNodes(new Set());
              setHighlightLinks(new Set());
              return;
            }
            handleSearchSelect(newValue);
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id} sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {option.primaryLabel}
              </Typography>
              {option.secondaryLabel && (
                <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                  {option.secondaryLabel}
                </Typography>
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search node..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {/* Toolbar buttons */}
        {/* Label mode toggle */}
        <Tooltip title={
          labelMode === 'english' ? 'Showing English label - click for notation'
          : labelMode === 'notation' ? 'Showing notation - click for original label'
          : 'Showing original label - click for English'
        }>
          <IconButton
            onClick={() =>
              setLabelMode((m) => (m === 'english' ? 'notation' : m === 'notation' ? 'original' : 'english'))
            }
            color="primary"
            sx={{ position: 'relative' }}
          >
            <Translate />
            <Box
              component="span"
              sx={{
                position: 'absolute',
                bottom: 2,
                right: 2,
                fontSize: '0.55rem',
                fontWeight: 700,
                lineHeight: 1,
                color: 'primary.main',
                bgcolor: 'white',
                borderRadius: '2px',
                px: '1px',
              }}
            >
              {labelMode === 'original' ? 'NL' : labelMode === 'english' ? 'EN' : 'ID'}
            </Box>
          </IconButton>
        </Tooltip>
        <Tooltip title="Toggle Filters">
          <IconButton onClick={() => setShowFilters(!showFilters)} color={showFilters ? 'primary' : 'default'}>
            <Badge badgeContent={Object.values(activeFilters).filter((v) => v !== null && (Array.isArray(v) ? v.length > 0 : true)).length} color="primary">
              <FilterList />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom In">
          <IconButton onClick={handleZoomIn}>
            <ZoomIn />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton onClick={handleZoomOut}>
            <ZoomOut />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fit to Screen">
          <IconButton onClick={handleCenterGraph}>
            <Fullscreen />
          </IconButton>
        </Tooltip>
        <Tooltip title="Center on Focus Subgraph">
          <IconButton onClick={handleCenterOnFocus}>
            <CenterFocusStrong />
          </IconButton>
        </Tooltip>
        <Tooltip title="Previous Subgraph">
          <span>
            <IconButton onClick={handleCenterPreviousSubgraph} disabled={orderedSubgraphs.length === 0}>
              <NavigateBefore />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Next Subgraph">
          <span>
            <IconButton onClick={handleCenterNextSubgraph} disabled={orderedSubgraphs.length === 0}>
              <NavigateNext />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
          height: '100%',
        }}
      >
        {/* Filters bar */}
        {showFilters && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, mr: 1 }}>
              Filters:
            </Typography>

            {/* Relationship type filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Relationship Type</InputLabel>
              <Select
                multiple
                value={activeFilters.relationship_type}
                label="Relationship Type"
                onChange={(e) => {
                  const value = e.target.value;
                  const nextValues =
                    typeof value === 'string' ? value.split(',') : value;

                  setActiveFilters((prev) => ({
                    ...prev,
                    relationship_type: nextValues,
                  }));
                }}
                renderValue={(selected) => selected.map((s) => EDGE_LABELS[s] || s).join(', ')}
              >
                {Object.entries(EDGE_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* SMART filter */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>SMART</InputLabel>
              <Select
                value={activeFilters.smart_compliant === null ? '' : String(activeFilters.smart_compliant)}
                label="SMART"
                onChange={(e) =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    smart_compliant: e.target.value === '' ? null : e.target.value === 'true',
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Compliant</MenuItem>
                <MenuItem value="false">Non-compliant</MenuItem>
              </Select>
            </FormControl>

            {/* Translation filter */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Translation</InputLabel>
              <Select
                value={activeFilters.translation_status || ''}
                label="Translation"
                onChange={(e) =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    translation_status: e.target.value || null,
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>

            {/* Validation filter */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Validation</InputLabel>
              <Select
                value={activeFilters.validation_status || ''}
                label="Validation"
                onChange={(e) =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    validation_status: e.target.value || null,
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="validated">Validated</MenuItem>
                <MenuItem value="not_validated">Not validated</MenuItem>
              </Select>
            </FormControl>

            <Button
              size="small"
              onClick={() =>
                setActiveFilters({
                  relationship_type: [],
                  smart_compliant: null,
                  translation_status: null,
                  validation_status: null,
                })
              }
            >
              Clear All
            </Button>
          </Paper>
        )}

        {/* Main content */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            overflow: 'hidden',
            minHeight: 0,
            width: '100%',
            height: '100%',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading graph data...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, p: 4 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : (
            <>
              {/* Graph canvas */}
              <Box
                ref={graphContainerRef}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <ForceGraph2D
                  ref={graphRef}
                  graphData={filteredGraphData}
                  width={graphDimensions.width || undefined}
                  height={graphDimensions.height || undefined}
                  backgroundColor="#f0f4f8"
                  nodeCanvasObject={nodeCanvasObject}
                  nodeCanvasObjectMode={() => 'replace'}
                  linkCanvasObject={linkCanvasObject}
                  linkCanvasObjectMode={() => 'replace'}
                  onNodeClick={handleNodeClick}
                  onNodeDragEnd={(node) => {
                    node.fx = node.x;
                    node.fy = node.y;
                  }}
                  onBackgroundClick={handleBackgroundClick}
                  onNodeRightClick={handleNodeExpand}
                  nodeLabel={(node) => {
                    const lbl =
                      labelMode === 'english'
                        ? (node.label_english || node.label_original || node.notation || node.label || '')
                        : labelMode === 'notation'
                        ? (node.notation || node.label || '')
                        : (node.label_original || node.label || node.notation || '');
                    return `${node.notation}\n${lbl}\n${(node.has_requirement || '').slice(0, 120)}`;
                  }}
                  linkDirectionalArrowLength={0}
                  enableNodeDrag={true}
                  enableZoomInteraction={true}
                  enablePanInteraction={true}
                  cooldownTicks={isHierarchicalLayout ? 70 : 250}
                  d3AlphaDecay={alphaDecay}
                  d3VelocityDecay={velocityDecay}
                  d3AlphaMin={0.001}
                  nodeRelSize={7}
                  warmupTicks={warmupTicks}
                  onEngineStart={() => {
                    if (graphRef.current && isHierarchicalLayout) {
                      graphRef.current.d3Force('charge').strength(-80).distanceMax(260);
                      const linkForce = graphRef.current.d3Force('link');
                      if (linkForce) linkForce.distance(150).strength(0.04);
                    }
                  }}
                  onDagError={() => {
                    // Graph has cycles — fall back to force-directed silently
                    if (activeLayout !== 'force-directed') {
                      const activeSubgraph = orderedSubgraphs[currentSubgraphIndex];
                      if (activeSubgraph?.nodeIds) {
                        activeSubgraphNodeIdsRef.current = new Set(activeSubgraph.nodeIds);
                      }
                      setActiveLayout('force-directed');
                    }
                  }}
                  {...layoutConfig}
                />

                {/* Legend */}
                <Paper
                  elevation={3}
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    p: 2,
                    borderRadius: 2,
                    maxWidth: 220,
                    bgcolor: 'rgba(255,255,255,0.97)',
                    backdropFilter: 'blur(6px)',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                    Relationship Types
                  </Typography>
                  <Stack spacing={0.75}>
                    {Object.entries(EDGE_COLORS).map(([key, color]) => (
                      <Box key={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.2 }}>
                          <Box sx={{ width: 16, height: 3, bgcolor: color, borderRadius: 1, flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color }}>
                            {EDGE_LABELS[key] || key}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', pl: '24px', display: 'block', lineHeight: 1.3 }}>
                          {EDGE_DESCRIPTIONS[key]}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Node Colors
                  </Typography>
                  <Stack spacing={0.3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#4CAF50' }} />
                      <Typography variant="caption">SMART Compliant</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FF9800' }} />
                      <Typography variant="caption">Not Compliant</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#F44336' }} />
                      <Typography variant="caption">Has Risks</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2196F3' }} />
                      <Typography variant="caption">Default</Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Node Shapes
                  </Typography>
                  <Stack spacing={0.3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2196F3' }} />
                      <Typography variant="caption">Requirement (same file)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, transform: 'rotate(45deg)', bgcolor: '#5C6BC0' }} />
                      <Typography variant="caption">Req. (cross-file) ◆</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: '#78909C' }} />
                      <Typography variant="caption">Document / External ▪</Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ my: 0.5 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Double-click / Right-click to expand or collapse
                  </Typography>
                </Paper>
              </Box>

              {/* Side panel: selected node detail */}
              {selectedNode && (
                <Paper
                  elevation={0}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: { xs: '100%', sm: 360, md: 380 },
                    maxWidth: '95vw',
                    zIndex: 5,
                    boxShadow: 3,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'white',
                  }}
                >
                  {/* Panel header */}
                  <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Node details</Typography>
                    <IconButton size="small" onClick={() => { setSelectedNode(null); setNodeDetail(null); }}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                    {/* Notation badge */}
                    {selectedNode.notation && (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: 'primary.main', color: 'white', borderRadius: 1, px: 1.5, py: 0.5, mb: 1.5, fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, userSelect: 'all' }}>
                        {selectedNode.notation}
                      </Box>
                    )}

                    {nodeDetailLoading && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CircularProgress size={14} />
                        <Typography variant="caption" color="text.secondary">Loading...</Typography>
                      </Box>
                    )}

                    {/* ── TEXT ── */}
                    {(nodeDetail || selectedNode) && (() => {
                      const d = nodeDetail;
                      const labelOrig = d?.label || selectedNode.label_original || selectedNode.label || '';
                      const labelEn = d?.label_english || selectedNode.label_english || '';
                      const textOrig = d?.requirement_text || d?.requirement_payload?.text || '';
                      const textEn = d?.requirement_text_english || d?.requirement_payload?.text_english || '';
                      const primaryLabel = labelEn || labelOrig;
                      const secondaryLabel = labelEn && labelOrig && labelEn !== labelOrig ? labelOrig : '';
                      const primaryRequirementText = textEn || textOrig;
                      const secondaryRequirementText = textEn && textOrig && textEn !== textOrig ? textOrig : '';
                      const notes = d?.notes || '';

                      return (
                        <>
                          {/* Labels */}
                          {primaryLabel && <Typography variant="body2" sx={{ fontWeight: 600, mb: secondaryLabel ? 0.2 : 1 }}>{primaryLabel}</Typography>}
                          {secondaryLabel && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>NL: {secondaryLabel}</Typography>}

                          {/* Status chips */}
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                            {(d?.is_validated ?? selectedNode.is_validated) && <Chip icon={<CheckCircle />} label="Validated" size="small" color="primary" variant="outlined" />}
                            {(d?.translation_completed ?? selectedNode.translation_completed) && <Chip label="Translated" size="small" color="info" variant="outlined" />}
                            {(d?.is_smart_compliant ?? selectedNode.is_smart_compliant) && <Chip icon={<CheckCircle />} label="SMART" size="small" color="success" variant="outlined" />}
                          </Stack>

                          {/* Requirement text */}
                          {(textOrig || textEn) && (
                            <>
                              <Divider sx={{ mb: 1 }} />
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Requirement text
                              </Typography>
                              {primaryRequirementText && (
                                <Typography variant="body2" sx={{ mb: secondaryRequirementText ? 0.4 : 0.75, lineHeight: 1.5 }}>{primaryRequirementText}</Typography>
                              )}
                              {secondaryRequirementText && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.5 }}>NL: {secondaryRequirementText}</Typography>
                              )}
                            </>
                          )}

                          {/* SMART Analysis */}
                          {d && d.smart_analysis_completed && (
                            <>
                              <Divider sx={{ my: 1.5 }} />
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                SMART Analysis — {d.smart_overall_score}/100
                              </Typography>
                              {[
                                { key: 'specific', label: 'Specific', score: d.smart_specific, reason: d.smart_specific_reasoning },
                                { key: 'measurable', label: 'Measurable', score: d.smart_measurable, reason: d.smart_measurable_reasoning },
                                { key: 'achievable', label: 'Achievable', score: d.smart_achievable, reason: d.smart_achievable_reasoning },
                                { key: 'relevant', label: 'Relevant', score: d.smart_relevant, reason: d.smart_relevant_reasoning },
                                { key: 'time_bound', label: 'Time-bound', score: d.smart_time_bound, reason: d.smart_time_bound_reasoning },
                              ].map(({ label, score, reason }) => (
                                <Box key={label} sx={{ mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>
                                    <Typography variant="caption" sx={{ color: score >= 70 ? 'success.main' : score >= 40 ? 'warning.main' : 'error.main', fontWeight: 700 }}>
                                      {Math.round(score)}
                                    </Typography>
                                  </Box>
                                  {/* Score bar */}
                                  <Box sx={{ height: 4, bgcolor: 'grey.200', borderRadius: 2, overflow: 'hidden', mb: 0.5 }}>
                                    <Box sx={{ height: '100%', width: `${score}%`, bgcolor: score >= 70 ? 'success.main' : score >= 40 ? 'warning.main' : 'error.main', borderRadius: 2 }} />
                                  </Box>
                                  {reason && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4, display: 'block' }}>{reason}</Typography>
                                  )}
                                </Box>
                              ))}
                            </>
                          )}

                          {d && !d.smart_analysis_completed && (
                            <>
                              <Divider sx={{ my: 1.5 }} />
                              <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                SMART analysis not executed
                              </Typography>
                            </>
                          )}

                          {/* Notes */}
                          {notes && (
                            <>
                              <Divider sx={{ my: 1.5 }} />
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Notes
                              </Typography>
                              <Typography variant="body2" sx={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{notes}</Typography>
                            </>
                          )}

                          {/* Expand/collapse button */}
                          {selectedNode.relationship_count > 0 && (() => {
                            const isNodeExpanded = compactMode
                              ? expandedNodes.has(selectedNode.id)
                              : !collapsedNodes.has(selectedNode.id);

                            return (
                              <>
                                <Divider sx={{ my: 1.5 }} />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<AccountTree />}
                                  onClick={() => handleNodeExpand(selectedNode)}
                                  disabled={expandingNode === selectedNode.id}
                                  fullWidth
                                >
                                  {expandingNode === selectedNode.id
                                    ? 'Updating...'
                                    : isNodeExpanded
                                      ? 'Collapse'
                                      : `Expand (${selectedNode.relationship_count} connections)`}
                                </Button>
                              </>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </Box>
                </Paper>
              )}
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GraphViewDialog;
